import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Shared helpers for the "Book me a caddie" (internal: tip-offer) screens.
 *
 * Tee dates/times are Bangkok wall-clock values. The API returns them as UTC
 * instants (`tf_date` is Bangkok midnight, e.g. "2026-02-27T17:00:00.000Z"), so
 * they are always rendered in Asia/Bangkok, whatever timezone the phone is in.
 */
export const BANGKOK_TZ = "Asia/Bangkok";

// The backend closes the selection window this many minutes before tee-off.
export const SELECTION_CUTOFF_MINUTES = 60;

export const OFFER_STATUS = {
  open: { color: "#2d6a4f", th: "เปิดรับ", en: "Open" },
  matched: { color: "#1677ff", th: "จับคู่แล้ว", en: "Matched" },
  expired: { color: "#8c8c8c", th: "หมดเวลา", en: "Expired" },
  cancelled: { color: "#d32f2f", th: "ยกเลิกแล้ว", en: "Cancelled" },
};

export const APPLICATION_STATUS = {
  applied: { color: "#fa8c16", th: "รอการเลือก", en: "Waiting" },
  selected: { color: "#1677ff", th: "ได้รับเลือก", en: "Selected" },
  not_selected: { color: "#8c8c8c", th: "ไม่ได้รับเลือก", en: "Not selected" },
};

// Tee-off transport labels, keyed by the backend tip_offers.play_mode values.
export const PLAY_MODE_LABELS = {
  cart: "รถกอล์ฟ (Cart)",
  walking: "เดิน (Walking)",
};

/** A play_mode value ('cart' | 'walking') as its bilingual label, or "-". */
export function formatPlayMode(value) {
  return PLAY_MODE_LABELS[value] || "-";
}

/**
 * Look up a status label, falling back to the raw status string.
 * @param {object} table - OFFER_STATUS or APPLICATION_STATUS.
 * @param {string} status
 */
export function statusLabel(table, status) {
  return table[status] || { color: "#8c8c8c", th: status, en: status };
}

export function formatOfferDate(value) {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.tz(BANGKOK_TZ).format("DD/MM/YYYY") : "-";
}

/** A timestamp (e.g. created_at) as "DD/MM/YYYY HH:mm" in Bangkok time. */
export function formatOfferDateTime(value) {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.tz(BANGKOK_TZ).format("DD/MM/YYYY HH:mm") : "-";
}

export function formatOfferTime(value) {
  if (!value) return "-";
  const t = dayjs(value);
  return t.isValid() ? t.tz(BANGKOK_TZ).format("HH:mm") : "-";
}

/**
 * Tee date/time as the `date` (YYYY-MM-DD) / `time` (HH:mm) query the booking
 * page expects, in Bangkok time.
 */
export function teeQuery(offer) {
  const date = dayjs(offer?.tf_date);
  const time = dayjs(offer?.tf_time);
  return {
    date: date.isValid() ? date.tz(BANGKOK_TZ).format("YYYY-MM-DD") : undefined,
    time: time.isValid() ? time.tz(BANGKOK_TZ).format("HH:mm") : undefined,
  };
}

export function formatCurrency(amount) {
  const value = Number(amount);
  if (amount === null || amount === undefined || Number.isNaN(value)) return "-";
  return new Intl.NumberFormat().format(value);
}

/**
 * Remaining time until `expireAt` as "H:MM:SS", or null once it has passed.
 * @param {string|Date} expireAt
 * @param {dayjs.Dayjs} [now]
 */
export function formatRemaining(expireAt, now = dayjs()) {
  if (!expireAt) return null;
  const ms = dayjs(expireAt).diff(now);
  if (!(ms > 0)) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Map an axios error from the tip-offer API to a bilingual `{ th, en }` message.
 * The backend's typed errors carry `{ error: { code, message: { th, en } } }`.
 * @param {any} error
 * @param {{th: string, en: string}} fallback
 */
export function apiErrorMessage(error, fallback) {
  const serverMsg = error?.response?.data?.error?.message;
  if (serverMsg && (serverMsg.th || serverMsg.en)) {
    return {
      th: serverMsg.th || serverMsg.en,
      en: serverMsg.en || serverMsg.th,
    };
  }
  return fallback;
}

export const GENERIC_ERROR = {
  th: "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง",
  en: "Something went wrong. Please try again.",
};
